import { Link } from "react-router-dom";
import { formatDate } from "../api";
import { addDays, todayIn } from "../date-range";
import { useApi } from "../hooks";
import { AreaChart, BarList, ChartCard, ChartEmpty } from "./Charts";

export type TrackingSummaryData = {
  period_days: number;
  period_start?: string;
  period_end?: string;
  tier: string | null;
  totals: {
    leads: number;
    forms: number;
    calls: number;
    /** Calls CallTrackingMetrics recorded. */
    ctm_calls?: number;
    /** Calls Google recorded through its forwarding numbers (Google call reporting). */
    google_calls?: number;
    google_share: number | null;
    /** Jobs matched to a call or lead. */
    booked: number;
    completed: number;
    revenue: number;
    /** Every job in the window from the export, matched or not (National Accounts excluded). */
    jobs_booked: number;
    jobs_completed: number;
    jobs_revenue: number;
    spend: number;
    cost_per_booked_job: number | null;
  };
  /** What each feed reaches, so an empty stretch reads as "not tracked then", not "no leads". */
  coverage?: {
    ctm: { status: "ok" | "stale" | "failing" | "needs_key" | "not_connected" | "unknown"; last_good_at: string | null; last_call: string | null };
    /** The first day of Google's own call records; null when the client has no Google Ads account. */
    google_calls_since: string | null;
    /** The days the job export covers, first to last booking; null when there is none. */
    jobs: { first_day: string; last_day: string } | null;
    /** The export reaches into these dates, so job counts (even 0) are real. */
    jobs_cover_period: boolean;
    sources: { google_ads: boolean; ctm: boolean; forms: boolean };
  };
  daily: { day: string; leads: number; forms: number; calls: number }[];
  funnel: { stage: string; value: number }[];
  by_channel: { channel: string; booked_jobs: number; revenue: number }[];
};

const shortDay = (d: string) =>
  new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const count = (v: number) => v.toLocaleString("en-US");

/** True when a CTM feed exists for the client and is not delivering. */
export const ctmDown = (s: TrackingSummaryData) => {
  const status = s.coverage?.ctm.status;
  return status === "needs_key" || status === "failing" || status === "stale";
};

/**
 * The first day of the window any lead feed was measuring, when that is later
 * than the window's start: Google's call records begin on one day for every
 * client, and with no forms source and no CTM delivering before it, the days
 * before are "not tracked", not "no leads". null when every day was measured.
 */
export function measuredFrom(s: TrackingSummaryData): string | null {
  const cover = s.coverage;
  const start = s.period_start;
  if (!cover || !start) return null;
  const googleFrom = cover.google_calls_since && cover.google_calls_since > start ? cover.google_calls_since : null;
  if (!googleFrom || cover.sources.forms) return null;
  // A CTM feed counted every day it was delivering: live now, or still live inside the window.
  if (cover.sources.ctm && (!ctmDown(s) || (cover.ctm.last_good_at ?? "").slice(0, 10) >= start)) return null;
  return googleFrom;
}

type FeedHealth = Partial<Record<"google_ads" | "ctm", { status?: string; last_good_at?: string | null }>>;

/**
 * The last day the call feeds were read for, when that is before the window's end: forms arrive
 * live, while CTM's calls and Google's call records reach only their last good sync (Google's
 * sync reads through the day before it ran). Later days are drawn as gaps, not as no leads.
 * null when a feed that counts is current, or nothing says otherwise.
 */
export function readThrough(s: TrackingSummaryData, health: FeedHealth | null | undefined): string | null {
  const cover = s.coverage;
  if (!cover || !health || cover.sources.forms || !s.period_end) return null;
  const ends: string[] = [];
  for (const key of ["google_ads", "ctm"] as const) {
    if (!cover.sources[key]) continue;
    const status = health[key]?.status || "unknown";
    if (status === "not_connected") continue; // never read anything: adds no days
    if (!["needs_key", "failing", "stale"].includes(status)) return null; // current (or unknown): every day was read
    const good = health[key]?.last_good_at;
    if (!good) continue;
    const day = todayIn(undefined, new Date(good));
    ends.push(key === "google_ads" ? addDays(day, -1) : day);
  }
  const last = ends.sort().at(-1);
  return last && last < s.period_end ? last : null;
}

/**
 * The lead-tracking charts on the client dashboard: leads per day, the funnel,
 * booked jobs by channel. The numbers themselves, and the dates, sit in the
 * dashboard's own "at a glance" block above.
 */
export default function TrackingCharts({ clientId, summary: s, rangeLabel }: { clientId: number; summary: TrackingSummaryData; rangeLabel: string }) {
  const t = s.totals;
  const cover = s.coverage;
  // The request the dashboard's connection notice makes (cached a minute): where the call feeds stop.
  const health = useApi<FeedHealth>(`/api/connections/health?client_id=${clientId}`);
  const through = readThrough(s, health.data);
  // Google's call records start on one day for every client; a window that starts
  // earlier draws zeros it never measured, so the chart says where they start.
  const googleSince = cover?.google_calls_since && s.period_start && cover.google_calls_since > s.period_start ? cover.google_calls_since : null;
  // The line starts on the first measured day, so untracked days never draw as a flat 0.
  const from = measuredFrom(s);
  const measured = from ? s.daily.filter((d) => d.day >= from) : s.daily;
  const daily = measured.length > 1 ? measured : s.daily;
  const trimmed = daily.length < s.daily.length;
  const jobsCovered = cover ? cover.jobs_cover_period : t.jobs_booked > 0;
  const pageViews = s.funnel.some((f) => f.stage === "Page views");
  // Without a job export for these dates the funnel stops at leads; with no page views either, there is
  // nothing to draw and the card says what is missing under the title it would have.
  const funnelTitle = pageViews ? (jobsCovered ? "From visit to completed job" : "From visit to lead") : "From lead to completed job";
  // A card with nothing to draw for these dates is left out rather than shown as an
  // empty box: the dashboard's notice above the tiles already says the job data stops.
  const showFunnel = Boolean((t.leads || t.jobs_booked) && s.funnel.length > 1) || jobsCovered;
  const showChannels = s.by_channel.length > 0 || t.jobs_booked > 0 || jobsCovered;
  return (
    <section className="tracking-dashboard" aria-label="Lead tracking">
      <div className="tracking-dashboard-head">
        <div>
          <h2>Where leads came from</h2>
        </div>
        <div className="detail-heading-actions">
          <Link className="button secondary" to={`/tracking?client_id=${clientId}`}>Open tracking</Link>
        </div>
      </div>
      <div className="tracking-chart-grid">
        <ChartCard
          eyebrow="Trend"
          title="Leads per day"
          meta={googleSince
            ? trimmed ? `From ${shortDay(daily[0].day)}, when Google call reporting starts` : `Google call reporting from ${formatDate(googleSince).replace(/, \d{4}$/, "")}`
            : "Forms and calls"}
          className="tracking-chart-wide"
        >
          {t.leads ? (
            <AreaChart
              integer
              data={daily.map((d) => ({ label: shortDay(d.day), value: through && d.day > through ? null : d.leads }))}
              format={(v) => `${Math.round(v)} lead${Math.round(v) === 1 ? "" : "s"}`}
              caption={trimmed ? `Leads per day from ${shortDay(daily[0].day)}, the first day tracked` : `Leads per day, ${rangeLabel || `last ${s.period_days} days`}`}
            />
          ) : (
            <ChartEmpty>{t.jobs_booked ? "No leads in these dates. Booked jobs below came from the job export." : "No leads in these dates."}</ChartEmpty>
          )}
          {t.leads > 0 && through && <p className="tracking-chart-note">Gaps are days the call feeds have not synced yet.</p>}
        </ChartCard>
        {showFunnel && (
          <ChartCard eyebrow="Funnel" title={funnelTitle} className={showChannels ? undefined : "tracking-chart-wide"}>
            {(t.leads || t.jobs_booked) && s.funnel.length > 1 ? (
              <BarList
                data={s.funnel.map((f) => ({ label: f.stage, value: f.value }))}
                format={count}
                caption={funnelTitle}
              />
            ) : (
              <ChartEmpty>Nothing to show for these dates.</ChartEmpty>
            )}
          </ChartCard>
        )}
        {showChannels && (
          <ChartCard eyebrow="Channels" title="Booked jobs by channel" className={showFunnel ? undefined : "tracking-chart-wide"}>
            {s.by_channel.length ? (
              <BarList
                data={s.by_channel.map((c) => ({ label: c.channel, value: c.booked_jobs }))}
                format={(v) => `${count(v)} job${v === 1 ? "" : "s"}`}
                caption="Booked jobs by the channel that brought them in"
              />
            ) : t.jobs_booked ? (
              // Jobs exist but none tied to a call or lead: matching is what is missing.
              <ChartEmpty>
                None of these {count(t.jobs_booked)} booked {t.jobs_booked === 1 ? "job" : "jobs"} matched a call or lead yet.{" "}
                <Link to={`/tracking?client_id=${clientId}`}>Rebuild matches</Link>
              </ChartEmpty>
            ) : (
              <ChartEmpty>No jobs were booked in these dates.</ChartEmpty>
            )}
          </ChartCard>
        )}
      </div>
    </section>
  );
}
